/**
 * Audit list criteria: program values map to one or more audit type values.
 * Used so "By Type" options reflect the selected "By Program" filter.
 */

const BLANK = { text: ' ', value: '' };

export const AUDIT_PROGRAMS = [
  BLANK,
  { text: 'Official Depository', value: 'OD' },
  { text: 'Warrants & Appropriations', value: 'war' },
  { text: 'Payroll Register', value: 'payroll' },
  { text: 'Miscellaneous Receipts', value: 'misc' },
  { text: 'Mortgage Tax', value: 'mtg' },
  { text: 'General Ledger', value: 'GL' },
  { text: 'System Config', value: 'SystemConfig' },
  { text: 'Tax Program', value: 'taxInquiry' },
  { text: 'Payment', value: 'taxPayment' },
];

/**
 * System config audit rows store `type` as the config scope (see SystemConfig logConfigAudit).
 * Source: system config scopes (e.g. tmc-local-auth-docker.systemconfigs.json).
 */
export const SYSTEM_CONFIG_AUDIT_TYPES = [
  { text: 'Global', value: 'global' },
  { text: 'Official Depository (OD)', value: 'odconfig' },
  { text: 'Warrants & Appropriations', value: 'warconfig' },
  { text: 'Miscellaneous Receipts', value: 'miscConfig' },
  { text: 'Mortgage Tax', value: 'mtgConfig' },
  { text: 'General Ledger', value: 'glConfig' },
  { text: 'Tax', value: 'taxConfig' },
  { text: 'Statement', value: 'statementConfig' },
  { text: 'Payment', value: 'paymentConfig' },
  { text: 'Tax Web', value: 'taxWebConfig' },
  { text: 'Apportionment', value: 'apportionmentConfig' },
  { text: 'Resale', value: 'resaleConfig' },
];

/** All type options (blank + every type). Shown when no program is selected. */
export const AUDIT_TYPES_ALL = [
  BLANK,
  { text: ' Official Depository Transaction', value: 'ODTransaction' },
  { text: 'Warrants & Appropriations', value: 'warTransaction' },
  { text: 'Warrants Account Transaction', value: 'warAccount' },
  { text: 'Payroll Register', value: 'payrollTransaction' },
  { text: 'Miscellaneous Receipts Transaction', value: 'miscTransaction' },
  { text: 'Mortgage Tax Transaction', value: 'mtgTransaction' },
  { text: 'General Ledger', value: 'glDailyTransaction' },
  { text: 'Tax Inquiry', value: 'taxInquiryTransaction' },
  { text: 'Tax Payment', value: 'taxpayments' },
  ...SYSTEM_CONFIG_AUDIT_TYPES,
];

/** Program value -> type values (excluding blank). */
export const AUDIT_PROGRAM_TO_TYPES = {
  OD: ['ODTransaction'],
  war: ['warTransaction', 'warAccount'],
  payroll: ['payrollTransaction'],
  misc: ['miscTransaction'],
  mtg: ['mtgTransaction'],
  GL: ['glDailyTransaction'],
  SystemConfig: SYSTEM_CONFIG_AUDIT_TYPES.map((t) => t.value),
  taxInquiry: ['taxInquiryTransaction'],
  taxPayment: ['taxpayments'],
};

/**
 * @param {string} programValue - criteria.program (empty = any program / show all types)
 * @returns {Array<{ text: string, value: string }>}
 */
export function getAuditTypesForProgram(programValue) {
  if (!programValue) {
    return AUDIT_TYPES_ALL;
  }
  const allowed = AUDIT_PROGRAM_TO_TYPES[programValue];
  if (!allowed || !allowed.length) {
    return [BLANK];
  }
  const byValue = new Map(
    AUDIT_TYPES_ALL.filter((t) => t.value !== '').map((t) => [t.value, t]),
  );
  const items = allowed.map((value) => byValue.get(value)).filter(Boolean);
  return [BLANK, ...items];
}

/**
 * @param {string} programValue
 * @param {string} typeValue - criteria.type
 * @returns {boolean}
 */
export function isAuditTypeAllowedForProgram(programValue, typeValue) {
  if (!typeValue) {
    return true;
  }
  if (!programValue) {
    return true;
  }
  const allowed = AUDIT_PROGRAM_TO_TYPES[programValue];
  return Boolean(allowed && allowed.includes(typeValue));
}
